/**
 * Composant d'affichage du QR code TOTP
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Affiche le QR code pour configurer l'authentification TOTP.
 */

  // Client Component pour Next.js

import { useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Props du composant QRCodeDisplay
 */
export interface QRCodeDisplayProps {
  /** URL du QR code (data URL ou URL externe) */
  qrCodeUrl: string;
  /** Secret TOTP en texte clair */
  secret: string;
  /** Nom de l'application (affiché dans les instructions) */
  appName?: string;
  /** Callback après copie du secret */
  onCopySecret?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant QRCodeDisplay
 *
 * @example
 * ```tsx
 * function TOTPSetup() {
 *   const { generateTOTPSecret, qrCodeUrl, secret } = use2FA();
 *
 *   return (
 *     <div>
 *       {qrCodeUrl && secret && (
 *         <QRCodeDisplay
 *           qrCodeUrl={qrCodeUrl}
 *           secret={secret}
 *           appName="Mon Application"
 *         />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function QRCodeDisplay({
  qrCodeUrl,
  secret,
  appName = 'cette application',
  onCopySecret,
  className = '',
}: QRCodeDisplayProps) {
  const [copied, setCopied] = useState(false);

  /**
   * Copier le secret dans le presse-papier
   */
  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopySecret?.();
    } catch (err) {
      toast.error('Impossible de copier le secret dans le presse-papiers');
    }
  };

  return (
    <div className={`bg-surface rounded-lg border border-border p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-content mb-4">
        Configurer l'authentification à deux facteurs
      </h3>

      {/* Instructions */}
      <div className="mb-6">
        <ol className="space-y-3 text-sm text-content-muted">
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-accent-light text-accent rounded-full text-xs font-semibold mr-3">
              1
            </span>
            <span>
              Téléchargez une application d'authentification comme{' '}
              <strong>Google Authenticator</strong>, <strong>Authy</strong>, ou{' '}
              <strong>Microsoft Authenticator</strong> sur votre téléphone.
            </span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-accent-light text-accent rounded-full text-xs font-semibold mr-3">
              2
            </span>
            <span>
              Scannez le QR code ci-dessous avec votre application, ou entrez manuellement le secret.
            </span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-accent-light text-accent rounded-full text-xs font-semibold mr-3">
              3
            </span>
            <span>
              Entrez le code à 6 chiffres généré par l'application pour valider la configuration.
            </span>
          </li>
        </ol>
      </div>

      {/* QR Code */}
      <div className="flex flex-col items-center mb-6">
        <div className="bg-surface p-4 rounded-lg border-2 border-border shadow-sm">
          <img
            src={qrCodeUrl}
            alt="QR Code pour l'authentification à deux facteurs"
            className="w-48 h-48"
          />
        </div>
        <p className="mt-3 text-xs text-content-subtle text-center max-w-xs">
          Scannez ce code avec votre application d'authentification
        </p>
      </div>

      {/* Secret manuel */}
      <div className="bg-surface-raised rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <label
            htmlFor="totp-secret"
            className="text-sm font-medium text-content-muted"
          >
            Secret TOTP (configuration manuelle)
          </label>
          <button
            type="button"
            onClick={copySecret}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-text hover:text-primary transition-colors"
            aria-label="Copier le secret"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5 mr-1 text-success" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Copié !
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copier
              </>
            )}
          </button>
        </div>
        <div className="relative">
          <input
            id="totp-secret"
            type="text"
            value={secret}
            readOnly
            className="w-full px-3 py-2 bg-surface border border-border rounded-md font-mono text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary select-all"
            aria-label="Secret TOTP"
          />
        </div>
        <p className="mt-2 text-xs text-content-subtle">
          Si vous ne pouvez pas scanner le QR code, copiez ce secret et ajoutez-le manuellement dans votre application.
        </p>
      </div>

      {/* Info supplémentaire */}
      <div className="mt-4 p-3 bg-accent-light border border-accent rounded-md">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-accent mt-0.5 mr-2 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-xs text-accent">
            Une fois configurée, l'authentification à deux facteurs renforcera la sécurité de votre compte.
            Vous devrez entrer un code à 6 chiffres à chaque connexion.
          </p>
        </div>
      </div>
    </div>
  );
}
